/**
 * Device-GNSS side of the seam. When the user picks "Device GNSS", this builds a
 * DeviceGnssSource (the browser Geolocation watch), registers it as the active
 * source, and starts it. Permission prompts and fixes flow through exactly the
 * same NMEA channel the simulator uses, so GnssIngest is unchanged. Status
 * (requesting / live / no fix / blocked) is pushed to the position-source store
 * for the UI.
 *
 * On the native (Capacitor Android) build we first ask for the OS location
 * permission via the Capacitor Geolocation plugin, because the WebView's
 * navigator.geolocation won't raise that Android dialog on its own. On the web
 * this bootstrap is a no-op and the browser prompts as usual.
 */
import { useEffect } from 'react';
import { useGnssStore } from '@/store/gnss';
import { usePositionSourceStore } from '@/store/positionSource';
import { useBackgroundServiceStore } from '@/store/backgroundService';
import { DeviceGnssSource, ensureNativeLocationPermission } from '@/gnss';

export function DeviceGnssFeed() {
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);
  // When the background-location service owns the seam (it delivers foreground
  // fixes too), stand the WebView watch down so we don't double-feed the ingest.
  const serviceRunning = useBackgroundServiceStore((s) => s.serviceRunning);

  useEffect(() => {
    if (sourceKind !== 'device' || serviceRunning) return;

    const setStatus = usePositionSourceStore.getState().setDeviceStatus;
    const source = new DeviceGnssSource({
      onStatus: (st, msg) => setStatus(st, msg ?? null),
      // The cached starting position went down the same pipe as a live fix and
      // so was stamped with the moment it arrived. Put its real time back, so
      // the boat is red or not on the honest age of the position under it rather
      // than on where it came from.
      onCachedPosition: (fixTimeMs) => useGnssStore.setState({ lastFixWallMs: fixTimeMs }),
    });
    useGnssStore.getState().setSource(source);

    let cancelled = false;
    setStatus('requesting', 'Asking for location permission…');
    void ensureNativeLocationPermission().then((res) => {
      if (cancelled) return;
      if (res === 'denied') {
        setStatus(
          'denied',
          'Location permission denied. Allow location for Ardent Fix in Android settings, then switch to Device GNSS again.',
        );
        return;
      }
      // 'granted' (native, permission held) or 'unavailable' (web / plugin
      // absent): start the watch, which reports its own live / no-fix / blocked
      // status from here.
      source.start();
    });

    return () => {
      cancelled = true;
      source.stop();
      if (useGnssStore.getState().source === source) {
        useGnssStore.getState().setSource(null);
      }
    };
  }, [sourceKind, serviceRunning]);

  return null;
}
